import React, { useState, useEffect, useMemo } from "react";
import { Input, Select, Button, Upload, Spin, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axiosInstance from "../../Config/axiosConfig";
import "./ClassDetails.css";

const { TextArea } = Input;
const { Option } = Select;

function ClassDetails({ classId, isEditing, classData, setActiveTab, teacherId }) {
  const [teacherUserId] = useState(teacherId);
  const [classTitle, setClassTitle] = useState("");
  const [classDescription, setClassDescription] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [projectFiles, setProjectFiles] = useState([]);
  const [classCategory, setClassCategory] = useState("");
  const [classLevel, setClassLevel] = useState("");
  const [educationCategory, setEducationCategory] = useState("");
  const [price, setPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [educationLevel, setEducationLevel] = useState([]);
  const [educationClasses, setEducationClasses] = useState([]);

  const findIdByName = (array, name) => {
    const item = array.find(item => item.name === name);
    return item ? item.id : null;
  };

  useEffect(() => {
    if (isEditing && classData) {
      setClassTitle(classData.title || "");
      setClassDescription(classData.description || "");
      setProjectDescription(classData.resource || "");
      setPrice(classData.price ? String(classData.price) : "");
      
      if (classData.subject_id) {
        setClassCategory(String(classData.subject_id));
      }
      if (classData.education_class_id) {
        setClassLevel(String(classData.education_class_id));
      }
      if (classData.education_level_id) {
        setEducationCategory(String(classData.education_level_id));
      }
    }
  }, [isEditing, classData]);

  useEffect(() => {
    if (isEditing && classData && subjects.length > 0 && educationLevel.length > 0 && educationClasses.length > 0) {
      if (classData.education_class && !classData.education_class_id) {
        const classId = findIdByName(educationClasses, classData.education_class);
        if (classId) setClassLevel(String(classId));
      }
      
      if (classData.subject && !classData.subject_id) {
        const subjectId = findIdByName(subjects, classData.subject);
        if (subjectId) setClassCategory(String(subjectId));
      }
      
      if (!classData.education_level_id && classData.education_class_id) {
        const classItem = educationClasses.find(c => c.id === classData.education_class_id);
        if (classItem) setEducationCategory(String(classItem.education_level_id));
      }
    }
  }, [isEditing, classData, subjects, educationLevel, educationClasses]);

  // Fetch subjects and education levels
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.post("/Public/get-lookups",
          {},
          { headers: { "Content-Type": "application/json" } }
        );
        
        if (response.data.code === 200 && response.data.data) {
          setSubjects(response.data.data.subjects || []);
          setEducationClasses(response.data.data.educationClasses || []);

          const storedEducationLevel = localStorage.getItem("InstructorEducationLevel");
          let parsedIds = [];
          
          if (storedEducationLevel) {
            try {
              parsedIds = JSON.parse(storedEducationLevel);
              if (!Array.isArray(parsedIds)) {
                throw new Error("Data is not an array");
              }
            } catch (error) {
              console.error("Invalid data in localStorage:", storedEducationLevel);
              message.error("Invalid education level data in localStorage. Using default values.");
              parsedIds = []; 
            }
          }

          const filteredEducationLevels = response.data.data.educationLevels.filter((level) =>
            parsedIds.includes(level.id)
          );
          setEducationLevel(
            filteredEducationLevels.length > 0 ? filteredEducationLevels : response.data.data.educationLevels || []
          );
        } else {
          message.error(response.data.message || "Failed to fetch data");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        message.error("Error fetching data: " + error.message);
      }
    };
    fetchData();
  }, []);

  const handleCreateClass = async () => {
    if (!classTitle || !classDescription || !projectDescription || !classCategory || !classLevel || !educationCategory || !price) {
      message.error("Please fill all required fields.");
      return;
    }

    const accessToken = localStorage.getItem("accessToken");
    const storedDeviceId = localStorage.getItem("deviceId");
    const storedUserIp = localStorage.getItem("userIp");

    if (!accessToken) {
      message.error("Authorization token missing.");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      
      // Append all required fields
      formData.append("title", classTitle); 
      formData.append("description", classDescription);
      formData.append("resource", projectDescription);
      formData.append("education_class_id", classLevel);
      formData.append("education_level_id", educationCategory);
      formData.append("subject_id", classCategory);
      formData.append("price", price);
      formData.append("instructor_user_id", teacherUserId);

      projectFiles.forEach((file) => {
        formData.append("resourceFile", file.originFileObj);
      });

      if (isEditing) {
        formData.append("id", classId);
      }

      // Debug output
      // console.log("FormData contents:");
      // for (let [key, value] of formData.entries()) {
      //   console.log(key, value); 
      // }

      const response = await axiosInstance.post(
        isEditing ? "/Class/update" : "/Class/create",
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            UserDevice: storedDeviceId,
            UserIPAddress: storedUserIp,
            'Content-Type': 'multipart/form-data'
            
          }
        }
      );

      // console.log('API response:', response.data);
      
      if (response?.data?.code === 200) {
        message.success(isEditing ? "Class updated successfully!" : "Class created successfully!");
        if (!isEditing && response?.data?.data?.classInfo?.id) {
          const newClassId = response.data.data.classInfo.id;
          setActiveTab("videos", newClassId);
        }
      } else {
        message.error(response?.data?.message || "Failed to save class");
      }
    } catch (error) {
      console.error("Error saving class:", {
        message: error.message,
        response: error.response?.data,
        config: error.config
      });
      message.error(
        error.response?.data?.message || 
        error.message || 
        "An error occurred while saving the class"
      );
    } finally {
      setLoading(false);
    }
  };

  // ... rest of your component code remains the same ...
  const subjectOptions = useMemo(
    () =>
      subjects.map(({ id, name }) => (
        <Option key={id} value={String(id)}>
          {name}
        </Option>
      )),
    [subjects]
  );

  const educationLevelOptions = useMemo(
    () =>
      educationLevel.map(({ id, name }) => (
        <Option key={id} value={String(id)}>
          {name}
        </Option>
      )),
    [educationLevel]
  );

  const educationClassOptions = useMemo(() => {
    if (educationCategory) {
      return educationClasses
        .filter((classItem) => classItem.education_level_id === Number(educationCategory))
        .map(({ id, name }) => (
          <Option key={id} value={String(id)}>
            {name}
          </Option>
        ));
    }
    return [];
  }, [educationCategory, educationClasses]);

  return (
    <div className="class-details-container">
      <h4>{isEditing ? "Edit Class Details" : "Class Details"}</h4>

      <label>Class Title:</label>
      <Input
        value={classTitle}
        onChange={(e) => setClassTitle(e.target.value)}
        placeholder="Enter class title"
        style={{ backgroundColor: "#f5f5f5", height: "50px" }}
      />

      <label>Class Description:</label>
      <TextArea
        value={classDescription}
        onChange={(e) => setClassDescription(e.target.value)}
        placeholder="Enter class description"
        rows={4}
        style={{ backgroundColor: "#f5f5f5", height: "150px" }}
      />

      <label>Project Description:</label>
      <TextArea
        value={projectDescription}
        onChange={(e) => setProjectDescription(e.target.value)}
        placeholder="Enter project description"
        rows={4}
        style={{ backgroundColor: "#f5f5f5", height: "150px" }}
      />

      <label>Price:</label>
      <Input
        type="number"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        placeholder="Enter price"
        style={{ backgroundColor: "#f5f5f5", height: "50px" }}
      />

      <label>Upload Project Files:</label>
      <Upload beforeUpload={() => false} onChange={({ fileList }) => setProjectFiles(fileList)} multiple>
        <Button icon={<UploadOutlined />}>Click to Upload</Button>
      </Upload>

      <label>Class Level:</label>
      <Select
        value={educationCategory}
        onChange={(value) => setEducationCategory(value)}
        placeholder="Select Level"
        style={{ width: "100%", backgroundColor: "#f5f5f5", }}
      >
        {educationLevelOptions}
      </Select>

      <label>Class:</label>
      <Select
        value={classLevel}
        onChange={(value) => setClassLevel(value)}
        placeholder="Select Level"
        style={{ width: "100%", backgroundColor: "#f5f5f5" }}
      >
        {educationClassOptions}
      </Select>

      <label>Subjects Category:</label>
      <Select
        value={classCategory}
        onChange={(value) => setClassCategory(value)}
        placeholder="Select Category"
        style={{ width: "100%", backgroundColor: "#f5f5f5" }}
      >
        {subjectOptions}
      </Select>

      <div className="buttons">
        <Button type="primary" className="save-btn" onClick={handleCreateClass} disabled={loading}>
          {loading ? <Spin /> : isEditing ? "Update" : "Next"}
        </Button>
      </div>
    </div>
  );
}

export default ClassDetails;